// src/components/settings/RegionManagementPanel.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { userService } from '../../services/adminServices'; // Adjust path
import { RefreshCw, XCircle, Edit, Save, Loader2 } from 'lucide-react'; // More icons

// Define the structure of the data received from the API
interface RegionManagementInfo {
  key: string; // Unique key for React list (e.g., "city-US-CA-Los Angeles")
  level: 'country' | 'state' | 'city';
  countryCode: string;
  stateCode: string | null;
  cityName: string | null;
  specificStatus: 'active' | 'waitlist' | 'coming_soon' | 'restricted' | 'default'; // 'default' means not explicitly set
  effectiveStatus: 'active' | 'waitlist' | 'coming_soon' | 'restricted';
  waitlistCount: number | null; // Only relevant for cities
}

// Define the possible statuses for the dropdown
const STATUS_OPTIONS: RegionManagementInfo['specificStatus'][] = [
    'active', 'waitlist', 'coming_soon', 'restricted', 'default' // Include 'default' to allow resetting
];

// Helper to get badge styles
const getStatusBadgeStyle = (status: RegionManagementInfo['effectiveStatus'] | RegionManagementInfo['specificStatus']) => {
    switch (status) {
        case 'active': return 'bg-green-100 text-green-800';
        case 'waitlist': return 'bg-yellow-100 text-yellow-800';
        case 'coming_soon': return 'bg-blue-100 text-blue-800';
        case 'restricted': return 'bg-red-100 text-red-800';
        case 'default': return 'bg-gray-100 text-gray-600 italic'; // Style for default/inherited
        default: return 'bg-gray-100 text-gray-800';
    }
};

// Helper to get display text
const getStatusDisplayText = (status: RegionManagementInfo['specificStatus']) => {
    if (status === 'default') return 'Default (Inherited)';
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()); // Capitalize words
}

function RegionManagementPanel() {
  const [regions, setRegions] = useState<RegionManagementInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRegionKey, setEditingRegionKey] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<RegionManagementInfo['specificStatus']>('default');

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    setEditingRegionKey(null);
    userService.getRegionManagementData()
      .then(data => {
        const regionsData = data?.regions as RegionManagementInfo[];

        if (Array.isArray(regionsData)) { // Check the asserted type
          setRegions(regionsData);
        } else {
          console.error("Invalid region data format or type assertion failed:", data);
          setError("Could not load region data.");
          setRegions([]);
        }
      })
      .catch(err => {
        console.error("Error fetching region data:", err);
        setError(err.message || "Failed to fetch region data.");
      })
      .finally(() => setLoading(false));
  }, []);



  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditClick = (region: RegionManagementInfo) => {
    setEditingRegionKey(region.key);
    setSelectedStatus(region.specificStatus); // Pre-fill dropdown with current specific status
  };

  const handleCancelEdit = () => {
    setEditingRegionKey(null);
  };

  const handleSaveStatus = async (region: RegionManagementInfo) => {
    if (!editingRegionKey || editingRegionKey !== region.key) return;

    const statusToSend = selectedStatus === 'default' ? 'waitlist' : selectedStatus;

    if (!window.confirm(`Confirm changing status for ${region.cityName || region.stateCode || region.countryCode} (${region.level}) to ${statusToSend}?`)) {
        return;
    }

    setLoading(true); // Indicate loading
    setError(null);

    try {
        // Call the service function
        await userService.updateRegionStatus({
            level: region.level,
            countryCode: region.countryCode,
            stateCode: region.stateCode ?? undefined,
            cityName: region.cityName ?? undefined,
            status: statusToSend,
        });
        // Success! Refetch data
        fetchData();
        // Optional: Show success toast

    } catch (err) {
        console.error("Error saving status:", err);
        setError(err instanceof Error ? err.message : "Failed to save status.");
        setLoading(false); // Stop loading only on error if fetchData isn't called
    }
     // No finally needed here as fetchData handles setLoading(false) on success
  };


  return (
    <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Region Activation Management</h2>
        <button
          onClick={fetchData}
          className="p-2 text-gray-500 rounded-full hover:bg-gray-100 disabled:opacity-50"
          disabled={loading}
          title="Refresh Regions"
        >
          <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {error} <button onClick={() => setError(null)} className="ml-4 font-bold">×</button>
        </div>
      )}

      {loading && editingRegionKey === null && ( // Show general loading only if not editing
        <div className="text-center py-10 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin inline-block mr-2"/> Loading region data...
        </div>
      )}

      {!loading || editingRegionKey !== null ? ( // Render table if not loading OR if editing
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 border">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Region</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Waitlist Count</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Specific Setting</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Effective Status</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {regions.length === 0 && !loading && (
                <tr><td colSpan={6} className="text-center py-6 text-sm text-gray-500">No regions discovered or defined yet.</td></tr>
              )}
              {regions.map((region: RegionManagementInfo) => (
                <tr key={region.key} className="hover:bg-gray-50">
                  {/* Region Name */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {region.cityName || region.stateCode || region.countryCode}
                  </td>
                  {/* Level */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{region.level}</td>
                  {/* Waitlist Count */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {region.level === 'city' ? (region.waitlistCount ?? 'N/A') : <span className="text-gray-300">-</span>}
                  </td>
                  {/* Specific Status Setting */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                     {editingRegionKey === region.key ? (
                         <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value as RegionManagementInfo['specificStatus'])}
                            className="block w-full pl-3 pr-10 py-1.5 text-xs border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                            disabled={loading} // Disable while main loading is happening
                         >
                            {STATUS_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{getStatusDisplayText(opt)}</option>
                            ))}
                         </select>
                     ) : (
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeStyle(region.specificStatus)}`}>
                            {getStatusDisplayText(region.specificStatus)}
                        </span>
                     )}
                  </td>
                  {/* Effective Status */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeStyle(region.effectiveStatus)}`}>
                      {getStatusDisplayText(region.effectiveStatus)}
                    </span>
                  </td>
                  {/* Actions */}
                  <td className="px-4 py-4 whitespace-nowrap text-sm space-x-2">
                    {editingRegionKey === region.key ? (
                        <>
                            <button
                                onClick={() => handleSaveStatus(region)}
                                className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                                title="Save Status"
                                disabled={loading} // Disable while main loading is happening
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : <Save className="h-4 w-4" />}
                            </button>
                             <button
                                onClick={handleCancelEdit}
                                className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                                title="Cancel Edit"
                                disabled={loading}
                            >
                                <XCircle className="h-4 w-4" />
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={() => handleEditClick(region)}
                            className="p-1 text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                            title="Edit Status Setting"
                            disabled={loading || editingRegionKey !== null} // Disable if general loading or another row is being edited
                        >
                            <Edit className="h-4 w-4" />
                        </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export default RegionManagementPanel;
